import { useEffect, useMemo, useRef, useState } from 'react';

const pad2 = (n: number) => String(n).padStart(2, '0');

const toIso = (d: Date) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fromIso = (s?: string) => {
    if (!s) return undefined;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
};

const sameDay = (a?: Date, b?: Date) =>
    !!a
    && !!b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const clampRange = (a?: Date, b?: Date) => {
    if (!a || !b) return { start: a, end: b };
    return a.getTime() <= b.getTime() ? { start: a, end: b } : { start: b, end: a };
};

const inRange = (d: Date, start?: Date, end?: Date) => {
    if (!start || !end) return false;
    const t = d.getTime();
    return t >= start.getTime() && t <= end.getTime();
};

const formatPill = (start?: string, end?: string, placeholder?: string) => {
    if (!start && !end) return placeholder || 'Select date range';
    if (start && !end) return start;
    if (!start && end) return end;
    return `${start} → ${end}`;
};

const CalendarIcon = () => (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
        <path
            d='M7 2v2M17 2v2M3.5 9h17M6 6h12a2.5 2.5 0 0 1 2.5 2.5v11A2.5 2.5 0 0 1 18 22H6a2.5 2.5 0 0 1-2.5-2.5v-11A2.5 2.5 0 0 1 6 6Z'
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

const ChevronLeft = () => (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
        <path d='M15 18l-6-6 6-6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

const ChevronRight = () => (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
        <path d='M9 6l6 6-6 6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

const XIcon = () => (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
        <path d='M18 6L6 18M6 6l12 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

const DateRangePicker = ({
    dateFrom,
    dateTo,
    placeholder,
    compact,
    onChange,
}: {
    dateFrom?: string;
    dateTo?: string;
    placeholder?: string;
    compact?: boolean;
    onChange: (next: { dateFrom?: string; dateTo?: string }) => void;
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const popRef = useRef<HTMLDivElement | null>(null);

    const start = useMemo(() => fromIso(dateFrom), [dateFrom]);
    const end = useMemo(() => fromIso(dateTo), [dateTo]);

    const [viewMonth, setViewMonth] = useState<Date>(() => {
        const base = start ?? new Date();
        return startOfMonth(base);
    });

    useEffect(() => {
        if (!open) return;
        const base = start ?? new Date();
        setViewMonth(startOfMonth(base));
    }, [open, start]);

    useEffect(() => {
        if (!open) return;

        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (popRef.current?.contains(t)) return;
            if (anchorRef.current?.contains(t)) return;
            setOpen(false);
        };

        document.addEventListener('mousedown', onDocDown);
        return () => document.removeEventListener('mousedown', onDocDown);
    }, [open]);

    const monthLabel = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
        return fmt.format(viewMonth);
    }, [viewMonth]);

    const cells = useMemo(() => {
        const first = startOfMonth(viewMonth);
        const startWeekday = first.getDay();
        const gridStart = new Date(first);
        gridStart.setDate(first.getDate() - startWeekday);

        const arr: Date[] = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(gridStart);
            d.setDate(gridStart.getDate() + i);
            arr.push(d);
        }
        return arr;
    }, [viewMonth]);

    const selectDay = (d: Date) => {
        const iso = toIso(d);

        if (!start || (start && end)) {
            onChange({ dateFrom: iso, dateTo: undefined });
            return;
        }

        const clamped = clampRange(start, d);
        onChange({
            dateFrom: clamped.start ? toIso(clamped.start) : undefined,
            dateTo: clamped.end ? toIso(clamped.end) : undefined,
        });
        setOpen(false);
    };

    const clear = () => {
        onChange({ dateFrom: undefined, dateTo: undefined });
    };

    const controlBase = compact
        ? 'w-full rounded-xl border bg-white dark:bg-black px-3 py-2 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-600'
        : 'w-full rounded-2xl border bg-white dark:bg-black px-4 py-3 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-600';

    return (
        <div className={`relative ${compact ? 'min-w-[200px]' : 'min-w-[240px]'}`}>
            <button
                ref={anchorRef}
                type='button'
                onClick={() => setOpen((v) => !v)}
                className={`${controlBase} flex items-center justify-between gap-3`}
            >
                <span className={`truncate ${!dateFrom && !dateTo ? 'text-gray-500 dark:text-gray-400' : ''}`}>
                    {formatPill(dateFrom, dateTo, placeholder)}
                </span>
                <span className='text-gray-400'>
                    <CalendarIcon />
                </span>
            </button>

            {open && (
                <div
                    ref={popRef}
                    className={`absolute right-0 z-50 mt-2 max-w-[90vw] rounded-3xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black shadow-xl ${compact ? 'w-[320px]' : 'w-[340px]'}`}
                >
                    <div className='p-3'>
                        <div className='flex items-center justify-between gap-2'>
                            <div className='px-3 py-2 rounded-xl text-sm border bg-[#00330f] text-white border-[#00330f] dark:bg-white dark:text-black dark:border-white'>
                                Range
                            </div>

                            <div className='flex items-center gap-2'>
                                <button
                                    type='button'
                                    onClick={clear}
                                    className='px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-borderColor-darkMode hover:opacity-90 focus:outline-none focus:ring-0'
                                >
                                    Clear
                                </button>
                                <button
                                    type='button'
                                    onClick={() => setOpen(false)}
                                    className='p-2 rounded-xl border border-gray-200 dark:border-borderColor-darkMode hover:opacity-90 focus:outline-none focus:ring-0'
                                    aria-label='Close'
                                >
                                    <XIcon />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className='px-3 pb-3'>
                        <div className='flex items-center justify-between'>
                            <button
                                type='button'
                                onClick={() => setViewMonth((d) => addMonths(d, -1))}
                                className='p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 focus:outline-none focus:ring-0'
                                aria-label='Previous month'
                            >
                                <ChevronLeft />
                            </button>

                            <div className='text-sm font-semibold text-black dark:text-white'>
                                {monthLabel}
                            </div>

                            <button
                                type='button'
                                onClick={() => setViewMonth((d) => addMonths(d, 1))}
                                className='p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 focus:outline-none focus:ring-0'
                                aria-label='Next month'
                            >
                                <ChevronRight />
                            </button>
                        </div>

                        <div className='mt-3 grid grid-cols-7 text-center text-[11px] font-medium text-gray-500 dark:text-gray-400'>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((w) => (
                                <div key={w} className='py-2'>
                                    {w}
                                </div>
                            ))}
                        </div>

                        <div className='grid grid-cols-7 gap-1'>
                            {cells.map((d) => {
                                const isThisMonth = d.getMonth() === viewMonth.getMonth();
                                const isStart = sameDay(d, start);
                                const isEnd = sameDay(d, end);
                                const isBetween = inRange(d, start, end) && !isStart && !isEnd;

                                const base =
                                    'h-10 w-10 mx-auto flex items-center justify-center rounded-xl text-sm focus:outline-none focus:ring-0 focus:ring-offset-0';

                                const muted = !isThisMonth ? 'text-gray-300 dark:text-gray-600' : 'text-black dark:text-white';
                                const between = isBetween ? 'bg-gray-100 dark:bg-white/10' : '';
                                const edge = (isStart || isEnd)
                                    ? 'bg-[#00330f] text-white dark:bg-white dark:text-black'
                                    : '';
                                const hover = !(isStart || isEnd) ? 'hover:bg-gray-100 dark:hover:bg-white/10' : '';

                                return (
                                    <button
                                        key={toIso(d)}
                                        type='button'
                                        onClick={() => selectDay(d)}
                                        className={`${base} ${muted} ${between} ${edge} ${hover}`}
                                        aria-label={toIso(d)}
                                    >
                                        {d.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
